import React, { useState } from 'react';
import { Container, Typography, Box, Button, Dialog, DialogContent, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Download, Close, ZoomIn } from '@mui/icons-material';
import PageWrapper from '../components/PageWrapper';

const BrochureCard = ({ imageUrl, alt, downloadFilename, isMobile, onImageClick }) => {
    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = `${process.env.PUBLIC_URL}${imageUrl}`;
        link.download = downloadFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 2,
            flex: 1,
            minWidth: 0,
            backgroundColor: '#f5f5f5',
            py: { xs: 2, sm: 3, md: 4 },
            px: { xs: 3, sm: 5, md: 6 },
            borderRadius: 2,
        }}>
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    backgroundColor: '#fff',
                    padding: { xs: 2, sm: 3 },
                    borderRadius: 1,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e0e0e0',
                    cursor: isMobile ? 'default' : 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        boxShadow: isMobile ? '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)' : '0 8px 20px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
                        transform: isMobile ? 'none' : 'translateY(-2px)',
                    }
                }}
                onClick={isMobile ? undefined : onImageClick}
            >
                {!isMobile && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(0, 0, 0, 0)',
                            transition: 'background-color 0.3s ease',
                            borderRadius: 1,
                            zIndex: 1,
                            pointerEvents: 'none',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            }
                        }}
                    >
                        <ZoomIn 
                            sx={{ 
                                fontSize: 64, 
                                color: 'white',
                                opacity: 0,
                                transition: 'opacity 0.3s ease',
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                                '.MuiBox-root:hover &': {
                                    opacity: 1
                                }
                            }} 
                        />
                    </Box>
                )}

                <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}${imageUrl}`}
                    alt={alt}
                    sx={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        borderRadius: 1,
                    }}
                />
            </Box>

            <Box sx={{ 
                display: 'flex', 
                gap: 2,
                flexWrap: 'wrap',
                justifyContent: 'center'
            }}>
                {!isMobile && (
                    <Button
                        variant="outlined"
                        color="primary"
                        size="large"
                        startIcon={<ZoomIn />}
                        onClick={onImageClick}
                        sx={{
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                        }}
                    >
                        View Full Size
                    </Button>
                )}
                <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    startIcon={<Download />}
                    onClick={handleDownload}
                    sx={{
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                    }}
                >
                    Download
                </Button>
            </Box>
        </Box>
    );
};

const HomeworkSupport = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [modalImage, setModalImage] = useState(null);

    const handleCloseModal = () => {
        setModalImage(null);
    };

    return (
        <PageWrapper configKey="homework-support" pageTitle="Homework Support">
            {(config) => {
                const brochures = config.brochures || (config.brochure ? [config.brochure] : []);

                return (
                    <Container maxWidth="lg" sx={{ mt: 4, mb: 6, px: { xs: 2, sm: 4 } }}>
                        <Typography 
                            variant="h3" 
                            component="h2" 
                            gutterBottom 
                            sx={{ 
                                mb: 1, 
                                textAlign: 'center'
                            }}
                        >
                            Homework Support
                        </Typography>

                        <Typography 
                            variant="h6" 
                            color="text.secondary"
                            sx={{ 
                                textAlign: 'center', 
                                mb: 4,
                                maxWidth: 600,
                                mx: 'auto',
                                fontWeight: 400,
                            }}
                        >
                            {isMobile 
                                ? 'Download to print and share!'
                                : 'Click an image or "View Full Size" to enlarge. Download to print and share!'
                            }
                        </Typography>

                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'center', md: 'flex-start' },
                            gap: { xs: 4, md: 4 },
                            justifyContent: 'center',
                        }}>
                            {brochures.map((brochure, index) => (
                                <BrochureCard
                                    key={index}
                                    imageUrl={brochure.imageUrl}
                                    alt={brochure.alt || 'Brochure'}
                                    downloadFilename={brochure.downloadFilename || 'brochure.png'}
                                    isMobile={isMobile}
                                    onImageClick={() => setModalImage(brochure)}
                                />
                            ))}
                        </Box>

                        <Dialog
                            open={modalImage !== null}
                            onClose={handleCloseModal}
                            maxWidth={false}
                            PaperProps={{
                                sx: {
                                    backgroundColor: 'transparent',
                                    boxShadow: 'none',
                                    maxWidth: '95vw',
                                    maxHeight: '95vh',
                                    overflow: 'hidden'
                                }
                            }}
                        >
                            <Box sx={{ position: 'relative' }}>
                                <IconButton
                                    onClick={handleCloseModal}
                                    sx={{
                                        position: 'absolute',
                                        top: -48,
                                        right: 0,
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 1)'
                                        },
                                        zIndex: 1
                                    }}
                                >
                                    <Close />
                                </IconButton>

                                <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                                    {modalImage && (
                                        <Box
                                            component="img"
                                            src={`${process.env.PUBLIC_URL}${modalImage.imageUrl}`}
                                            alt={`${modalImage.alt || 'Brochure'} - Full Size`}
                                            sx={{
                                                maxWidth: '100%',
                                                maxHeight: '95vh',
                                                width: 'auto',
                                                height: 'auto',
                                                display: 'block',
                                                borderRadius: 1,
                                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                                            }}
                                        />
                                    )}
                                </DialogContent>
                            </Box>
                        </Dialog>
                    </Container>
                );
            }}
        </PageWrapper>
    );
};

export default HomeworkSupport;
